import * as d3 from 'd3'
import d3Tip from 'd3-tip'

import { ChartView } from '../ChartView'

/**
 * Class which represents a BarChart model used in the BarChart vue component
 */
export class BarChartView extends ChartView {
  constructor(chartId, dataset, chartType, size, margin) {
    super(chartId, dataset, chartType, size, margin)
    this.legendOptions = {
      rectSize: 20,
      spacing: 5,
      textSize: 16,
    }

    d3.tip = d3Tip
  }

  /**
   * Draw a bar chart
   */
  draw() {
    //Creating bar charts legends
    this.createLegends()

    //Creating bar chart according to the current dataset
    this.createChart()

    //Add y axis title
    this.svg
      .append('text')
      .attr('class', 'title y-axis-title')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', 0 - this.height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text(this.yTitle)
  }

  /**
   * Create chart
   */
  createChart() {
    super.createChart()

    let that = this

    this.svg.select('#barchart-' + this.chartId).remove()
    this.g = this.svg.append('g').attr('id', 'barchart-' + this.chartId)

    var x = d3
        .scaleBand()
        .rangeRound([0, this.width])
        .padding(0.5),
      y = d3.scaleLinear().rangeRound([this.height, 0])

    x.domain(
      this.sortedDataset.items.map(function(d) {
        return that.getBarChartAxisValues(d)
      }),
    )

    y.domain([0, this.getYMaxValue()])

    this.xAxis = d3.axisBottom(x)
    this.yAxis = d3
      .axisLeft(y)
      .ticks(5)
      .tickSizeInner(-this.width)
      .tickSizeOuter(0)

    let wrapWidth =
      this.sortedDataset.items.length > 0
        ? this.width / this.sortedDataset.items.length - 10
        : this.width - this.margin.left
    let maxLabelNumberLines = 0
    this.g
      .append('g')
      .attr('class', 'chart-axis bar-chart-axis')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(this.xAxis)
      .selectAll('.tick text')
      .call(function(d) {
        d.each(function() {
          //Wrap text for used x axis label
          let text = d3.select(this),
            words = text
              .text()
              .split(/\s+/)
              .reverse(),
            word,
            line = [],
            lineNumber = 0,
            lineHeight = 1.1, // ems
            y = text.attr('y'),
            dy = parseFloat(text.attr('dy')),
            tspan = text
              .text(null)
              .append('tspan')
              .attr('x', 0)
              .attr('y', y)
              .attr('dy', dy + 'em')

          // eslint-disable-next-line no-cond-assign
          while ((word = words.pop())) {
            line.push(word)
            tspan.text(line.join(' '))
            if (tspan.node().getComputedTextLength() > wrapWidth) {
              line.pop()
              tspan.text(line.join(' '))
              line = [word]
              tspan = text
                .append('tspan')
                .attr('x', 0)
                .attr('y', y)
                .attr('dy', ++lineNumber * lineHeight + dy + 'em')
                .text(word)
            }
          }
          if (maxLabelNumberLines < lineNumber) {
            maxLabelNumberLines = lineNumber
          }
        })
      })

    //Resizing SVG height and legends offset according to the wrapped x axis labels
    let yLabelsOffset = maxLabelNumberLines * 20
    this.svg.select('.chart-legends').attr('transform', 'translate(0, ' + yLabelsOffset + ')')
    this.svgHeight = this.height + this.margin.top + this.margin.bottom * 2 + yLabelsOffset
    if (!this.config.display_options || this.config.display_options.legend != 'none') {
      this.svgHeight += this.chartLegends.rows * this.chartLegends.rectSize
    }
    d3.select('#svg-' + this.chartId).attr('viewBox', '0 0 ' + this.svgWidth + ' ' + this.svgHeight)

    //Append X and Y axis
    this.g
      .append('g')
      .attr('class', 'chart-axis bar-chart-axis')
      .call(this.yAxis)
      .append('text')
      .attr('y', 6)
      .attr('dy', '0.71em')
      .attr('text-anchor', 'end')
      .attr('transform', 'translate(0, 0)')

    d3.selectAll('.bar-chart-axis line').attr('class', 'tick-line')

    //Append vertical bars
    this.g
      .selectAll('.bar')
      .data(this.sortedDataset.items)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', function(d) {
        return x(that.getBarChartAxisValues(d))
      })
      .attr('y', this.height)
      .attr('width', x.bandwidth())
      .attr('fill', function(d) {
        return that.colors[d.category]
      })
      .on('mouseover', this.tooltip.show)
      .on('mouseout', this.tooltip.hide)
      .attr('height', 0)
      .transition()
      .duration(200)
      .attr('y', function(d) {
        return y(d.count)
      })
      .attr('height', function(d) {
        return that.height - y(d.count)
      })

    //Display grid
    this.displayGrid()
  }

  /**
   * Create legends and his colors according to chart's data
   */
  createLegends() {
    let legends = Array.from(this.dataset.items, item => {
      return item.category
    })

    // Used colors for the legend
    let legendColors = []
    this.dataset.items.forEach(item => {
      legendColors.push(this.colors[item.category])
    })

    super.createLegends(legends, legendColors)
    this.chartLegends.initTooltip()
  }

  /**
   * Return text avalues of the x axis
   * @param {*} d
   */
  getBarChartAxisValues(d) {
    return this.getLegendText(d.category)
  }

  /**
   * Return legend opacity according to his checked status
   * @param {*} d
   * @param {*} i
   */
  getLegendOpacity(d, i) {
    super.getLegendOpacity(d, i)
    return this.checked.indexOf(this.dataset.items[i].category) > -1 ? '1.0' : '0.2'
  }

  /**
   * Return if legend is checked by the user
   * @param {*} d
   * @param {*} i
   */
  isLegendChecked(d, i) {
    super.isLegendChecked(d, i)
    return this.checked.indexOf(this.dataset.items[i].category) > -1 ? 'true' : 'false'
  }

  /**
   * On Click listener for displayed legends
   * @param {*} clickedLegend
   * @param {*} d
   */
  clickOnLegend(clickedLegend, d, i) {
    super.clickOnLegend(clickedLegend, d)
    let isChecked = clickedLegend.attr('checked')
    isChecked = isChecked == 'true' ? 'false' : 'true'
    clickedLegend.attr('checked', isChecked)
    clickedLegend.style('opacity', clickedLegend.attr('checked') == 'true' ? '1.0' : '0.2')

    //Legend is checked ?
    if (isChecked == 'true') {
      this.checked.push(this.dataset.items[i].category)
      this.unchecked.splice(this.unchecked.indexOf(this.dataset.items[i].category), 1)
    } else {
      this.checked.splice(this.checked.indexOf(this.dataset.items[i].category), 1)
      this.unchecked.push(this.dataset.items[i].category)

      let checkedItem = this.dataset.items[i]
      this.svg
        .select(`#svg-${this.chartId} #legend-text-${i}`)
        .text(this.getLegendText(checkedItem.category))
    }

    this.sortedDataset.items = this.dataset.items.filter(item => {
      return this.checked.indexOf(item.category) > -1
    })

    //We recalculate the total count to display a valid percentage for each categories
    var total = this.sortedDataset.items.reduce((sum, item) => {
      return item ? sum + item.count : sum
    }, 0)

    this.sortedDataset.items = this.sortedDataset.items.map(item => {
      var percent = Math.round((item.count / total) * 100)

      //Display the new percentage to the proper legend's category
      var legendIndex = this.dataset.items.findIndex(function(item2) {
        return item2.category === item.category
      })

      this.svg
        .select(`#svg-${this.chartId} #legend-text-${legendIndex}`)
        .text(this.getLegendText(item.category))

      return {
        category: item.category,
        count: item.count,
        percent: percent,
      }
    })

    if (this.otherCategories) {
      this.otherCategories = this.otherCategories.map(other => {
        var percent = Math.round((other.count / total) * 100)
        return {
          category: other.category,
          count: other.count,
          percent: percent,
        }
      })
    }

    //Redrawing chart
    this.createChart()
  }

  /**
   *
   * @param {*} otherCategories
   */
  initTooltip(otherCategories) {
    let that = this

    d3.selectAll('.d3-tip').style('opacity', 0)

    this.otherCategories = otherCategories

    this.tooltip = d3
      .tip()
      .attr('class', 'd3-tip')
      .html(function(d, i) {
        let item = that.sortedDataset.items.find(item => {
          return d.data ? item.category == d.data.category : item.category == d.category
        })
        if (!item) {
          item = that.dataset.items[i]
        }
        item = that.sortedDataset.items.find(i => {
          return i.category == item.category
        })
        let htmlTooltip = ''
        //Tooltip for other categories values
        if (item) {
          if (item.category === 'others') {
            that.otherCategories.forEach(item => {
              if (item.category != 'others') {
                htmlTooltip +=
                  that.getTooltipText(item.category, item.count, item.percent) + '<br/>'
              }
            })
          } else {
            //Tooltip for normal value
            htmlTooltip = that.getTooltipText(item.category, item.count, item.percent)
          }
        }

        return htmlTooltip
      })

    this.tooltip.hide
    this.tooltip.offset([-5, 0])
    if (this.svg && this.tooltip && this.svg.call(this.tooltip)) {
      this.svg.call(this.tooltip).style('margin-bottom: 20px')
    }
  }
}
